import {
	type ReactNode,
	type RefObject,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { Modal } from "./Modal.tsx";
import useIsMobileViewport from "../hooks/useIsMobileViewport.ts";

export const ResponsivePopover = ({
	id,
	modalHeader,
	modalBody,
	placement,
	popoverContent,
	popoverHeader,
	renderTarget,
	toggle,
}: {
	id: string;
	modalHeader: ReactNode;
	modalBody: ReactNode;
	placement?: "auto" | "top" | "bottom" | "left" | "right";
	popoverContent: ReactNode;
	/** Optional Popover.Header for the desktop popover rendering. */
	popoverHeader?: ReactNode;
	renderTarget: (props: {
		onClick?: () => void;
		forwardedRef?: RefObject<HTMLElement | null>;
	}) => ReactNode;
	toggle?: () => void;
}) => {
	const [showModal, setShowModal] = useState(false);
	const [showPopover, setShowPopover] = useState(false);
	const showPopoverRef = useRef(false);
	const insidePointerTransactionRef = useRef(false);
	const insidePointerResetFrameRef = useRef<number | undefined>(undefined);
	const targetRef = useRef<HTMLElement>(null);
	const wrapperRef = useRef<HTMLSpanElement>(null);
	const isMobile = useIsMobileViewport();
	const focusTarget = useCallback(() => {
		window.requestAnimationFrame(() => {
			const target =
				targetRef.current ??
				wrapperRef.current?.querySelector<HTMLElement>(
					"button, [href], input, select, textarea, [tabindex]",
				);
			target?.focus();
		});
	}, []);
	const setDesktopPopoverOpen = useCallback(
		(nextShow: boolean) => {
			if (showPopoverRef.current === nextShow) {
				return;
			}
			showPopoverRef.current = nextShow;
			setShowPopover(nextShow);
			if (nextShow) {
				toggle?.();
			} else {
				focusTarget();
			}
		},
		[focusTarget, toggle],
	);
	const handleDesktopTargetClick = useCallback(() => {
		setDesktopPopoverOpen(!showPopoverRef.current);
	}, [setDesktopPopoverOpen]);

	useEffect(() => {
		if (isMobile || !showPopover) {
			return;
		}

		// Overlay replaces the ref on a React element while wiring Popper, so a ref
		// supplied directly to <Popover> does not identify the live portal root.
		// Resolve our unique id for every event instead. This also keeps internal
		// controls inside the same root across content-only React rerenders.
		const eventIsInsideCurrentPopover = (event: Event) => {
			if (!(event.target instanceof Node)) {
				return false;
			}
			const popover = document.getElementById(id);
			return (
				wrapperRef.current?.contains(event.target) === true ||
				popover?.contains(event.target) === true
			);
		};
		const clearInsidePointerTransaction = () => {
			insidePointerTransactionRef.current = false;
			insidePointerResetFrameRef.current = undefined;
		};
		const scheduleInsidePointerTransactionClear = () => {
			if (!insidePointerTransactionRef.current) {
				return;
			}
			if (insidePointerResetFrameRef.current !== undefined) {
				window.cancelAnimationFrame(insidePointerResetFrameRef.current);
			}
			insidePointerResetFrameRef.current = window.requestAnimationFrame(
				clearInsidePointerTransaction,
			);
		};
		const handleDocumentPointerDown = (event: PointerEvent) => {
			if (eventIsInsideCurrentPopover(event)) {
				insidePointerTransactionRef.current = true;
				return;
			}
			clearInsidePointerTransaction();
			setDesktopPopoverOpen(false);
		};
		const handleDocumentPointerEnd = () => {
			scheduleInsidePointerTransactionClear();
		};
		const handleDocumentFocusIn = (event: FocusEvent) => {
			if (
				!eventIsInsideCurrentPopover(event) &&
				!insidePointerTransactionRef.current
			) {
				setDesktopPopoverOpen(false);
			}
		};
		const handleDocumentKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				setDesktopPopoverOpen(false);
			}
		};

		document.addEventListener("pointerdown", handleDocumentPointerDown);
		document.addEventListener("pointerup", handleDocumentPointerEnd);
		document.addEventListener("pointercancel", handleDocumentPointerEnd);
		document.addEventListener("focusin", handleDocumentFocusIn);
		document.addEventListener("keydown", handleDocumentKeyDown);
		return () => {
			document.removeEventListener("pointerdown", handleDocumentPointerDown);
			document.removeEventListener("pointerup", handleDocumentPointerEnd);
			document.removeEventListener("pointercancel", handleDocumentPointerEnd);
			document.removeEventListener("focusin", handleDocumentFocusIn);
			document.removeEventListener("keydown", handleDocumentKeyDown);
			if (insidePointerResetFrameRef.current !== undefined) {
				window.cancelAnimationFrame(insidePointerResetFrameRef.current);
			}
			clearInsidePointerTransaction();
		};
	}, [id, isMobile, setDesktopPopoverOpen, showPopover]);

	if (isMobile) {
		return (
			<>
				{renderTarget({
					onClick: () => {
						setShowModal(true);
						if (toggle) {
							toggle();
						}
					},
				})}
				<Modal
					centered
					show={showModal}
					onHide={() => {
						setShowModal(false);
					}}
				>
					<Modal.Header closeButton>{modalHeader}</Modal.Header>
					<Modal.Body>{modalBody}</Modal.Body>
				</Modal>
			</>
		);
	}

	return (
		<OverlayTrigger
			trigger={[]}
			placement={placement ?? "auto"}
			show={showPopover}
			overlay={
				<Popover id={id}>
					{popoverHeader ? (
						<Popover.Header as="h3">{popoverHeader}</Popover.Header>
					) : null}
					<Popover.Body>{popoverContent}</Popover.Body>
				</Popover>
			}
		>
			<span ref={wrapperRef}>
				{renderTarget({
					forwardedRef: targetRef,
					onClick: handleDesktopTargetClick,
				})}
			</span>
		</OverlayTrigger>
	);
};
